import React from 'react';
import { Link } from 'react-router-dom';
import {
    logMonthlyCardViewed,
    logMonthlyInviteModalOpened,
    logMonthlyInviteCopyLink,
    logMonthlyGiftsUpsellClicked,
    logMonthlyLinkClicked,
} from '../../../utils/monthlyGiftAnalytics';
import {
    logGgPlusMonthlyGiftsGateViewed,
    logGgPlusUpgradeClicked,
} from '../../../utils/ggPlusAnalytics';

import Monthly_invite_modal from './Monthly_invite_modal.js';
import monthly_gifts_illustration from '../../../images/illustrations/recurring_gift_green_bg_100.png';

import './Monthly_gifts_card.css';
import { membershipUrl } from '../../../utils/membershipUrl.js';

/**
 * Plan-page "Monthly Gifts" card — GG+ subscriber vs upsell variants.
 *
 * Props:
 *   - plan_id              : number — child_account id
 *   - child_name           : string — account_name of the plan
 *   - is_gg_plus           : boolean — owner has GG+ (shows subscriber card vs upsell)
 *   - show_on_gifting_page : boolean — show_monthly_gifting_option on gifting page
 */
class Monthly_gifts_card extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            invite_modal_open: false,
        };
        this.openInviteModal  = this.openInviteModal.bind(this);
        this.closeInviteModal = this.closeInviteModal.bind(this);
        this.handleCopyLink   = this.handleCopyLink.bind(this);
        this.handleUpgrade    = this.handleUpgrade.bind(this);
    }

    hasGgPlus() {
        if (this.props.is_gg_plus != null) return !!this.props.is_gg_plus;
        return !!this.props.is_premium;
    }

    componentDidMount() {
        const isGgPlus = this.hasGgPlus();
        logMonthlyCardViewed({
            source: 'plan page',
            plan_id: this.props.plan_id,
            is_gg_plus: isGgPlus,
        });
        if (!isGgPlus) {
            logGgPlusMonthlyGiftsGateViewed({
                source: 'monthly_gifts_plan',
                plan_id: this.props.plan_id,
            });
        }
    }

    // Build the monthly-gift link.
    buildMonthlyLink() {
        let user = {};
        try { user = JSON.parse(localStorage.getItem('user')) || {}; } catch (e) { user = {}; }
        const baseUrl = window.location.origin || 'https://app.greatestgiftapp.com';
        const params = new URLSearchParams();
        if (this.props.child_name) params.set('Gift_for', this.props.child_name);
        if (user.first_name) params.set('Send_to', user.first_name);
        if (user.email) params.set('send_to_email', user.email);
        if (this.props.plan_id != null) params.set('account_id', String(this.props.plan_id));
        if (user.id != null) params.set('ref', String(user.id));
        params.set('channel', 'email');
        params.set('frequency', 'monthly');
        return `${baseUrl}/give-a-gift?${params.toString()}`;
    }

    openInviteModal() {
        logMonthlyInviteModalOpened({ plan_id: this.props.plan_id });
        try {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            window.scrollTo(0, 0);
        }
        this.setState({ invite_modal_open: true });
    }

    closeInviteModal() {
        this.setState({ invite_modal_open: false });
    }

    handleCopyLink() {
        const link = this.buildMonthlyLink();
        logMonthlyLinkClicked({ source: 'plan card', plan_id: this.props.plan_id });
        logMonthlyInviteCopyLink({ source: 'plan card', plan_id: this.props.plan_id });
        navigator.clipboard.writeText(link)
            .then(() => alert('The monthly gift link was copied!'))
            .catch(() => alert('There was an issue when attempting to copy the link.'));
    }

    handleUpgrade() {
        logMonthlyGiftsUpsellClicked({ plan_id: this.props.plan_id });
        logGgPlusUpgradeClicked({ source: 'monthly_gifts_plan', plan_id: this.props.plan_id });
    }

    renderGgPlusSubscriber() {
        const settingsPath = `/plan/${this.props.plan_id}/gifting_page?focus=show_monthly_gifting_option`;
        const visibilityLabel = this.props.show_on_gifting_page ? 'Hide from Gifting Page' : 'Show on Gifting Page';
        return (
            <div className='card monthly_gifts_card'>
                <p className='bold monthly_gifts_card_title'>Monthly Gifts</p>
                <img
                    src={monthly_gifts_illustration}
                    alt=''
                    className='monthly_gifts_card_illustration'
                />
                <p className='monthly_gifts_card_body'>
                    Invite your closest circle to give automatically each month.
                </p>
                <p className='monthly_gifts_card_body'>
                    Perfect for those who want to contribute regularly over time.
                </p>

                <div className='monthly_gifts_card_rows'>
                    <div
                        className='monthly_gifts_card_row'
                        onClick={this.handleCopyLink}
                        role='button'
                        tabIndex='0'
                    >
                        <span className='monthly_gifts_card_row_label'>Copy Monthly Gifts Link</span>
                        <i className='material-icons-outlined monthly_gifts_card_row_icon'>link</i>
                    </div>

                    <div
                        className='monthly_gifts_card_row'
                        onClick={this.openInviteModal}
                        role='button'
                        tabIndex='0'
                    >
                        <span className='monthly_gifts_card_row_label'>Invite with a GG Email</span>
                        <i className='material-icons-outlined monthly_gifts_card_row_icon'>mail_outline</i>
                    </div>

                    <Link className='monthly_gifts_card_row monthly_gifts_card_row_link' to={settingsPath}>
                        <span className='monthly_gifts_card_row_label'>{visibilityLabel}</span>
                        <i className='material-icons-outlined monthly_gifts_card_row_icon'>arrow_forward_ios</i>
                    </Link>
                </div>

                <Monthly_invite_modal
                    open={this.state.invite_modal_open}
                    onClose={this.closeInviteModal}
                    plan_id={this.props.plan_id}
                    child_name={this.props.child_name}
                    parent_name={(() => {
                        try { return (JSON.parse(localStorage.getItem('user')) || {}).first_name || ''; }
                        catch (e) { return ''; }
                    })()}
                    monthly_link={this.buildMonthlyLink()}
                />
            </div>
        );
    }

    renderUpsell() {
        const childDisplay = (this.props.child_name || '').trim() || 'them';
        return (
            <div className='card monthly_gifts_card monthly_gifts_card_upsell'>
                <p className='bold monthly_gifts_card_title'>Monthly Gifts</p>
                <img
                    src={monthly_gifts_illustration}
                    alt=''
                    className='monthly_gifts_card_illustration'
                />
                <p className='monthly_gifts_card_body'>
                    Perfect for the closest circle who want to contribute regularly over time.
                </p>
                <p className='monthly_gifts_card_body'>
                    A simple way for {childDisplay}'s closest circle to keep showing up for his future.
                </p>
                <Link
                    to={membershipUrl({
                        source: 'monthly_gifts_plan',
                        returnTo: `/plan/${this.props.plan_id}`,
                    })}
                    className='bold green_text monthly_gifts_card_upgrade_cta'
                    onClick={this.handleUpgrade}
                >
                    Upgrade to GG+
                </Link>
            </div>
        );
    }

    render() {
        return this.hasGgPlus() ? this.renderGgPlusSubscriber() : this.renderUpsell();
    }
}

export default Monthly_gifts_card;
